import React from "react";
import { useLocation, useHistory, useParams, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { BASE_URL } from "../services/config";
import { Button, Card, CardBody, CardFooter, Divider, Input, Slider, Checkbox, CheckboxGroup } from "@heroui/react";
import NewsletterSection from "../components/newsletter-section";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useCart } from "../contexts/cart-context";
import { pushImpressions, pushProductClick } from '../services/ecommerce';
import { useCategories, useProductsByCategory, useCatalog, useAromas, useWaxes, useColors, useHoldersRaw } from "../hooks/use-catalog";
import { ErrorDisplay, LoadingDisplay } from "../components/error-boundary";
import catalogBackground from "../assets/images/catalog-banner.png";
import heroNewYearBackground from "../assets/images/hero-new-year-background.png";
import imageLoadFailed from "../assets/images/image-load-failed.png";

interface CatalogPageProps {
  onCategorySelect: (category: string) => void;
  onProductSelect: (productId: string | number, categoryName: string) => void;
  onAddToCart?: (productName: string) => void; // Add missing prop
}

interface Product {
  id: number;
  name: string;
  slug?: string;
  category: string;
  scent: string;
  aroma?: { name?: string } | any;
  article?: string | number;
  price: number;
  image: string;
  labels?: string[];
  quantity?: number; // optional - backend may provide stock quantity
  is_exclusive?: boolean;
  new_year?: boolean;
}

interface Category {
  key: string;
  name: string;
  image: string;
  description: string;
}

export const CatalogPage: React.FC<CatalogPageProps> = ({ 
  onCategorySelect, 
  onProductSelect,
  onAddToCart = () => {} 
}) => {
  // price formatter with non-breaking thousands separator per ru-RU
  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('ru-RU').format(value);
    } catch (e) {
      return String(value);
    }
  };
  const location = useLocation();
  const history = useHistory();
  const params: { categoryKey?: string } = useParams();
  const isCollectionPage = params.categoryKey === 'collection';
  
  // Get category from URL path (`/catalog/:categoryKey`) with fallback to query param for backward compatibility
  const getInitialCategory = () => {
    if (params.categoryKey) return params.categoryKey;
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('category');
  };

  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(getInitialCategory());
  const [priceRange, setPriceRange] = React.useState<[number, number]>([1, 30000]);
  const [selectedScents, setSelectedScents] = React.useState<string[]>([]);
  const [selectedWax, setSelectedWax] = React.useState<string[]>([]);
  const [selectedColor, setSelectedColor] = React.useState<string[]>([]);
  const [selectedHolder, setSelectedHolder] = React.useState<string[]>([]);
  const [selectedCollection, setSelectedCollection] = React.useState<string | null>(() => {
    const initialSearch = typeof window !== 'undefined' ? window.location.search : location.search;
    const searchParams = new URLSearchParams(initialSearch || '');
    if (params.categoryKey === 'collection' || searchParams.get('new_year') === 'true') {
      return 'new_year';
    }
    return null;
  });
  const [searchQuery, setSearchQuery] = React.useState("");
  const [catalogSearchValue, setCatalogSearchValue] = React.useState("");
  // search query for aroma server-side search (debounced in hook)
  const [aromaQuery, setAromaQuery] = React.useState('');
  // Show filters on desktop, hide by default on mobile — determine on mount
  const [showFilters, setShowFilters] = React.useState(false);
  const filtersVisible = showFilters && !isCollectionPage;
  const [sortOption, setSortOption] = React.useState<string>("popular");
  
  // Use the catalog hooks instead of hardcoded data
  const { categories, isLoading: categoriesLoading, error: categoriesError } = useCategories();
  // Build filters object supported by backend and memoize to avoid refetch loops
  const DEFAULT_PRICE_RANGE: [number, number] = [1, 30000];
  const filters = React.useMemo(() => {
    const f: Record<string, any> = {};
    // price filters only when they differ from defaults
    if (priceRange && (priceRange[0] !== DEFAULT_PRICE_RANGE[0] || priceRange[1] !== DEFAULT_PRICE_RANGE[1])) {
      f.price_min = priceRange[0];
      f.price_max = priceRange[1];
    }
    if (selectedScents && selectedScents.length) {
      // backend accepts comma-separated ids/names; here we send names selected from UI
      f.aroma = selectedScents.join(',');
    }
    if (selectedWax && selectedWax.length) {
      f.wax = selectedWax.join(',');
    }
    if (selectedColor && selectedColor.length) {
      f.color = selectedColor.join(',');
    }
    if (selectedHolder && selectedHolder.length) {
      f.holder = selectedHolder.join(',');
    }
    if (selectedCollection) {
      // основной флаг коллекции для сервера
      f.collection = selectedCollection;
      if (selectedCollection === 'new_year') {
        f.new_year = true;
      }
    }
    if (searchQuery && String(searchQuery).trim().length) {
      f.q = String(searchQuery).trim();
    }
    return f;
  }, [priceRange, selectedScents, selectedWax, selectedColor, selectedHolder, searchQuery, selectedCollection]);

  // Determine current category and its type (candles / diffusers / other)
  const currentCategory = React.useMemo(() => {
    if (!selectedCategory) return null;
    return categories.find(cat => cat.key === selectedCategory) || null;
  }, [categories, selectedCategory]);

  const isCandleCategory = React.useMemo(() => {
    if (!currentCategory) return false;
    const key = (currentCategory.key || '').toLowerCase();
    const name = (currentCategory.name || '').toLowerCase();
    // treat anything with "candle" or "свеч" as candle category
    return key.includes('candle') || name.includes('свеч');
  }, [currentCategory]);

  const isDiffuserCategory = React.useMemo(() => {
    if (!currentCategory) return false;
    const key = (currentCategory.key || '').toLowerCase();
    const name = (currentCategory.name || '').toLowerCase();
    return key.includes('diffuser') || name.includes('диффуз');
  }, [currentCategory]);

  const isSachetCategory = React.useMemo(() => {
    if (!currentCategory) return false;
    const key = (currentCategory.key || '').toLowerCase();
    const name = (currentCategory.name || '').toLowerCase();
    return key.includes('sachet') || name.includes('саше');
  }, [currentCategory]);

  const isAutoPerfumeCategory = React.useMemo(() => {
    if (!currentCategory) return false;
    const key = (currentCategory.key || '').toLowerCase();
    const name = (currentCategory.name || '').toLowerCase();
    return key.includes('auto') || name.includes('авто');
  }, [currentCategory]);

  // Categories where holder filter is not applicable (e.g. sachets, decorative candles)
  const isHolderNotRelevantCategory = React.useMemo(() => {
    if (!currentCategory) return false;
    const key = (currentCategory.key || '').toLowerCase();
    const name = (currentCategory.name || '').toLowerCase();
    // sachets and decorative candles should not show holder filter
    const isDecorativeCandle = (key.includes('decor') || name.includes('декоратив')) && (key.includes('candle') || name.includes('свеч'));
    return isSachetCategory || isDecorativeCandle;
  }, [currentCategory, isSachetCategory]);

  const { products, isLoading: productsLoading, isLoadingMore, error: productsError, next, count, loadNext, refetch } = useProductsByCategory(selectedCategory, filters);
  const { catalog } = useCatalog();

  // Локальный флаг «только что сменили категорию» для плавного UX
  const [categoryJustChanged, setCategoryJustChanged] = React.useState(false);

  // Update URL when category changes (new canonical format: /catalog/:categoryKey)
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasNewYear = searchParams.get('new_year') === 'true';

    if (selectedCollection === 'new_year' && !hasNewYear) {
      searchParams.set('new_year', 'true');
    }
    if (selectedCollection !== 'new_year' && hasNewYear) {
      searchParams.delete('new_year');
    }

    const nextPath = selectedCategory ? `/catalog/${selectedCategory}` : '/catalog';
    const nextSearch = searchParams.toString();
    const normalizedCurrentSearch = location.search.replace(/^\?/, '');

    if (location.pathname !== nextPath || normalizedCurrentSearch !== nextSearch) {
      history.replace({
        pathname: nextPath,
        search: nextSearch ? `?${nextSearch}` : '',
      });
    }
  }, [selectedCategory, selectedCollection, history, location.pathname, location.search]);

  // Update category when URL changes
  React.useEffect(() => {
    const categoryParam = getInitialCategory();
    if (categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam);
      // навигация по адресу (извне) — сбрасываем локальный флаг
      setCategoryJustChanged(false);
    }
  }, [location, params.categoryKey, selectedCategory]);

  // Автоматически включаем фильтр новогодней коллекции при переходе на /catalog/collection или ?new_year=true
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldEnable = isCollectionPage || searchParams.get('new_year') === 'true';
    setSelectedCollection(prev => {
      if (shouldEnable && prev !== 'new_year') {
        return 'new_year';
      }
      if (!shouldEnable && prev === 'new_year') {
        return null;
      }
      return prev;
    });
  }, [isCollectionPage, location.search]);

  // Get current category name for display
  const getCurrentCategoryName = () => {
    if (selectedCategory === 'collection') return "новогодней коллекции";
    if (!selectedCategory) return "каталогу";
    return categories.find(cat => cat.key === selectedCategory)?.name || "каталогу";
  };

  // Load aromas from API (preferred) — fallback to unique scents derived from catalog

  const { aromas, isLoading: aromasLoading, isLoadingMore: aromasLoadingMore, next: aromasNext, loadNext: loadAromasNext, reset: resetAromas } = useAromas(aromaQuery);
  const { items: waxes } = useWaxes();
  const { items: colors } = useColors();
  const [holders, setHolders] = React.useState<Array<{ id?: number; name: string }>>([]);
  const [holdersLoading, setHoldersLoading] = React.useState(false);
  const [holdersError, setHoldersError] = React.useState<Error | null>(null);

  // Load holders list with proper type depending on category
  React.useEffect(() => {
    const loadHolders = async () => {
      setHoldersLoading(true);
      setHoldersError(null);
      try {
        const type = isCandleCategory
          ? 'holder'
          : isAutoPerfumeCategory
            ? 'auto_flacon'
            : isDiffuserCategory
              ? 'flacon'
              : '';

        const data = await useHoldersRaw(type ? { type } : {});
        setHolders(Array.isArray((data as any).results) ? (data as any).results : []);
      } catch (err) {
        setHolders([]);
        setHoldersError(err instanceof Error ? err : new Error('Failed to fetch holders'));
      } finally {
        setHoldersLoading(false);
      }
    };

    loadHolders();
  }, [isCandleCategory, isDiffuserCategory, isAutoPerfumeCategory]);
  // local UI state to toggle showing all aromas for fallback (when API aromas not available)
  const [showAllAromas, setShowAllAromas] = React.useState(false);
  // Collapsible filter sections (collapsed by default)
  const [openFilters, setOpenFilters] = React.useState<{ price: boolean; aroma: boolean; wax: boolean; color: boolean; holder: boolean; collection: boolean }>({ price: true, aroma: false, wax: false, color: false, holder: false, collection: false });

  const toggleFilterSection = (key: 'price' | 'aroma' | 'wax' | 'color' | 'holder' | 'collection') => {
    setOpenFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Build fallback uniqueScents (used only if aromas API returns empty)
  const getAllScents = React.useCallback(() => {
    const scentsSet = new Set<string>();
    Object.values(catalog).forEach(categoryProducts => {
      categoryProducts.forEach(product => {
        const scentStr = typeof product.scent === 'string'
          ? product.scent
          : ((product as any).aroma && typeof (product as any).aroma.name === 'string')
            ? (product as any).aroma.name
            : '';
        (scentStr || '').split(', ').forEach(scent => {
          if (scent && scent.trim()) scentsSet.add(scent.trim());
        });
      });
    });
    return Array.from(scentsSet).sort();
  }, [catalog]);

  const uniqueScents = getAllScents();
  
  // Get filtered products based on selected category, price range, scents, and search query
  const getFilteredProducts = () => {
    if (!selectedCategory || !products) return [];

    // Если есть хоть один серверный фильтр (включая коллекции) — полностью доверяем бэку
    const serverFiltersActive = Boolean(
      filters.q ||
      filters.aroma ||
      filters.price_min ||
      filters.price_max ||
      filters.in_stock ||
      // коллекции/специальные флаги
      filters.collection ||
      filters.new_year
    );

    const baseList = Array.isArray(products) ? products : [];

    // Если серверные фильтры активны — никаких доп. клиентских фильтров, только сортировка
    if (serverFiltersActive) {
      return [...baseList].sort((a, b) => {
        if (sortOption === "price_asc") return a.price - b.price;
        if (sortOption === "price_desc") return b.price - a.price;
        if (sortOption === "name") return a.name.localeCompare(b.name);
        return a.id - b.id;
      });
    }

    // Иначе — старый полностью клиентский путь (когда сервер не фильтрует вообще)
    const filtered = baseList.filter(product => {
      const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];

      // normalize scent string for filtering
      const scentStr = typeof product.scent === 'string'
          ? product.scent
          : ((product as any).aroma && typeof (product as any).aroma.name === 'string')
            ? (product as any).aroma.name
            : '';
      // split by comma or semicolon and normalize to lower-case trimmed tokens
      const productScents = (scentStr || '')
        .split(/[,;]+/)
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
      const selectedScentsNormalized = selectedScents.map(s => (s || '').toString().trim().toLowerCase());
      const matchesScent = selectedScents.length === 0 || productScents.some(scent => selectedScentsNormalized.includes(scent));

      const q = searchQuery.trim().toLowerCase();
      const matchesSearch = q === "" ||
        (product.name || '').toLowerCase().includes(q) ||
        (scentStr || '').toLowerCase().includes(q) ||
        (product.category || '').toLowerCase().includes(q);

      return matchesPrice && matchesScent && matchesSearch;
    });

    return filtered.sort((a, b) => {
      if (sortOption === "price_asc") {
        return a.price - b.price;
      } else if (sortOption === "price_desc") {
        return b.price - a.price;
      } else if (sortOption === "name") {
        return a.name.localeCompare(b.name);
      }
      // Default: popular (by id)
      return a.id - b.id;
    });
  };

  const filteredProducts = getFilteredProducts();

  const handleCatalogSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = catalogSearchValue.trim();
    if (!trimmed) return;
    history.push(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  // Push impressions when the product list (filteredProducts) or category changes
  React.useEffect(() => {
    try {
      if (Array.isArray(filteredProducts) && filteredProducts.length > 0) {
        const dlProducts = filteredProducts.map((p, idx) => ({
          id: p.slug || p.id,
          name: p.name,
          price: p.price,
          category: p.category,
          sku: (p as any).article || undefined,
          position: idx + 1,
          list: selectedCategory || 'catalog'
        }));
        pushImpressions(dlProducts, selectedCategory || 'catalog');
      }
    } catch (e) {
      // ignore analytics failures
    }
  }, [filteredProducts, selectedCategory]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Add cart context
  const { addItem } = useCart();

  // Image error fallback (inline SVG placeholder)
  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.currentTarget;
    if (!img) return;
    // set fallback image and mark as failed so overlay can appear
    try {
      img.src = imageLoadFailed;
    } catch (err) {
      // ignore
    }
    img.setAttribute('data-failed', 'true');
  };

  // Updated function to handle product selection with router
  const handleProductClick = (productIdOrSlug: string | number, slug?: string | null) => {
    const idOrSlug = slug || productIdOrSlug;
    history.push(`/product-detail/${idOrSlug}`);
  };
  
  // Add function to go back to all categories
  const handleBackToCategoriesClick = () => {
    setSelectedCategory(null);
    // Reset filters
    setPriceRange([1, 30000]);
    setSelectedScents([]);
    setSelectedWax([]);
    setSelectedColor([]);
    setSelectedHolder([]);
    setSelectedCollection(null);
    setSearchQuery("");
  };

  // Add missing handleCategorySelect function
  const handleCategorySelect = (categoryKey: string) => {
    setSelectedCategory(categoryKey);
    setCategoryJustChanged(true);
    onCategorySelect(categoryKey);
    // Reset filters when changing categories
    setPriceRange([1, 30000]);
    setSelectedScents([]);
    setSelectedWax([]);
    setSelectedColor([]);
    setSelectedHolder([]);
    setSelectedCollection(null);
    setSearchQuery("");
  };

  // Add missing handleResetFilters function
  const handleResetFilters = () => {
    // Reset to default range
    setPriceRange(DEFAULT_PRICE_RANGE);
    setSelectedScents([]);
    setSelectedWax([]);
    setSelectedColor([]);
    setSelectedHolder([]);
    setSelectedCollection(null);
    setSearchQuery("");
    // Also clear aroma search input and reset aromas list
    setAromaQuery('');
    try {
      resetAromas();
    } catch (err) {
      // ignore if resetAromas not available for some reason
    }
  };

  // Detect viewport width and auto-toggle filters: show on lg+ (>=1024px), hide on smaller
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setShowFilters(mq.matches);
    update();
    // add listener
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  // hide filters altogether on collection page
  React.useEffect(() => {
    if (isCollectionPage && showFilters) {
      setShowFilters(false);
    }
  }, [isCollectionPage, showFilters]);

  return (
    <>
      <Helmet>
        <title>{selectedCategory ? `${getCurrentCategoryName()} SAFONOVA - Каталог` : 'Каталог ароматической продукции SAFONOVA'}</title>
        <meta name="description" content={selectedCategory ? `Посмотрите товары в категории ${getCurrentCategoryName()}` : 'Полный каталог продукции Интернет-магазина SAFONOVA — свечи, диффузоры, саше и другие ароматические товары'} />
        <link rel="canonical" href={`${BASE_URL || ''}${location.pathname}`} />
        <meta property="og:title" content={selectedCategory ? `${getCurrentCategoryName()} SAFONOVA - Каталог` : 'Каталог ароматической продукции SAFONOVA'} />
        <meta property="og:description" content={selectedCategory ? `Посмотрите товары в категории ${getCurrentCategoryName()}` : 'Полный каталог продукции Интернет-магазина SAFONOVA — свечи, диффузоры, саше и другие ароматические товары'} />
        <meta property="og:url" content={`${BASE_URL || ''}${location.pathname}`} />
      </Helmet>
      {/* Hero Banner */}
      <section 
        className="relative flex items-center py-32 md:py-40"
        style={{
          backgroundImage: isCollectionPage
            ? `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.2)), url('${heroNewYearBackground}')`
            : `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.18)), url('${catalogBackground}')`,
          backgroundPosition: isCollectionPage ? "center 60%" : "center 70%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}
      >
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {isCollectionPage ? (
              <>
                <p className="uppercase tracking-[0.3em] text-white/70 text-sm mb-4">LIMITED EDITION</p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
                  Новогодняя <span className="font-semibold">коллекция 2026</span>
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
                  Праздничные ароматы, подарочные наборы и свечи, созданные специально для тёплых зимних историй.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
                  Каталог <span className="font-semibold">продукции</span>
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
                  Изучите наш ассортимент ароматической продукции ручной работы из премиальных материалов
                </p>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Catalog Content */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          {selectedCategory ? (
            // Product List View
            <>
              <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
                <Link to="/catalog">
                  <Button
                    variant="flat"
                    color="default"
                    startContent={<Icon icon="lucide:arrow-left" width={18} />}
                  >
                    Все категории
                  </Button>
                </Link>

                <div className="flex items-center gap-4 justify-end flex-1">
                  {!isCollectionPage && (
                    <Button
                      variant="flat"
                      color={filtersVisible ? "primary" : "default"}
                      startContent={<Icon icon="lucide:sliders-horizontal" width={20} />}
                      onPress={() => setShowFilters(!showFilters)}
                    >
                      {filtersVisible ? "Скрыть фильтры" : "Показать фильтры"}
                    </Button>
                  )}

                  <div className="relative">
                    <select 
                      className="bg-content2 border border-divider text-foreground py-2 px-4 pr-8 appearance-none cursor-pointer focus:outline-none focus:border-primary"
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                    >
                      <option value="popular">Популярные</option>
                      <option value="price_asc">По цене ↑</option>
                      <option value="price_desc">По цене ↓</option>
                      <option value="name">По названию</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-foreground">
                      <Icon icon="lucide:chevron-down" width={16} />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Filters Panel */}
                {filtersVisible && (
                  <div className="col-span-1">
                    {/* Make filters panel sticky on scroll for larger viewports */}
                    <div className="sticky top-24 self-start">
                      <Card className="border border-divider">
                        <CardBody className="p-6">
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="text-lg font-medium">Фильтры</h3>
                          <Button
                            variant="flat"
                            color="default"
                            size="sm"
                            onPress={handleResetFilters}
                          >
                            Сбросить
                          </Button>
                        </div>

                        <div className="space-y-6">
                          {/* Price Range Filter (collapsible) */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-base">Цена</h4>
                              <button
                                aria-expanded={openFilters.price}
                                aria-controls="filter-price"
                                onClick={() => toggleFilterSection('price')}
                                className="p-1 text-foreground/60 hover:text-foreground transition-transform"
                              >
                                <Icon icon="lucide:chevron-down" width={18} className={openFilters.price ? 'transform rotate-180' : 'transform rotate-0'} />
                              </button>
                            </div>

                            {openFilters.price && (
                              <div id="filter-price">
                                {/* helper to clamp values */}
                                <>
                                  <Slider 
                                    label="Диапазон цен"
                                    step={1}
                                    minValue={1}
                                    maxValue={30000}
                                    value={priceRange}
                                    // Slider onChange provides number | number[] depending on implementation — normalize to [number, number]
                                    onChange={(val: number | number[]) => {
                                      const clamp = (v: number) => Math.max(1, Math.min(30000, Math.round(v)));
                                      if (Array.isArray(val)) {
                                        setPriceRange([clamp(Number(val[0] || 0)), clamp(Number(val[1] || 0))]);
                                      } else {
                                        // if single number provided, set lower bound and keep upper
                                        setPriceRange([clamp(Number(val || 0)), priceRange[1]]);
                                      }
                                    }}
                                    className="max-w-md"
                                  />

                                  <div className="flex items-center gap-3 mt-3">
                                    <div className="flex-1">
                                      <label className="text-sm text-foreground/70 mb-1 block">Мин. цена</label>
                                      <Input
                                        placeholder="Мин"
                                        value={String(priceRange[0] ?? '')}
                                        onValueChange={(v: string) => {
                                          const n = parseInt(v.replace(/[^0-9]/g, ''), 10);
                                          if (Number.isNaN(n)) {
                                            setPriceRange([1, priceRange[1]]);
                                          } else {
                                            const clamped = Math.max(1, Math.min(30000, n));
                                            setPriceRange([clamped, Math.max(clamped, priceRange[1])]);
                                          }
                                        }}
                                        className="w-full"
                                      />
                                    </div>

                                    <div className="flex-1">
                                      <label className="text-sm text-foreground/70 mb-1 block">Макс. цена</label>
                                      <Input
                                        placeholder="Макс"
                                        value={String(priceRange[1] ?? '')}
                                        onValueChange={(v: string) => {
                                          const n = parseInt(v.replace(/[^0-9]/g, ''), 10);
                                          if (Number.isNaN(n)) {
                                            setPriceRange([priceRange[0], 30000]);
                                          } else {
                                            const clamped = Math.max(1, Math.min(30000, n));
                                            setPriceRange([Math.min(priceRange[0], clamped), clamped]);
                                          }
                                        }}
                                        className="w-full"
                                      />
                                    </div>
                                  </div>

                                  <div className="flex justify-between mt-2 text-sm text-foreground/70">
                                    <span>{priceRange[0]} ₽</span>
                                    <span>{priceRange[1]} ₽</span>
                                  </div>
                                </>
                              </div>
                            )}
                          </div>

                          {/* Scent Filter (load from API aromas list and show only 4 by default) - collapsible */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-base">Аромат</h4>
                              <button
                                aria-expanded={openFilters.aroma}
                                aria-controls="filter-aroma"
                                onClick={() => toggleFilterSection('aroma')}
                                className="p-1 text-foreground/60 hover:text-foreground transition-transform"
                              >
                                <Icon icon="lucide:chevron-down" width={18} className={openFilters.aroma ? 'transform rotate-180' : 'transform rotate-0'} />
                              </button>
                            </div>

                            {openFilters.aroma && (
                              <div id="filter-aroma">
                                {/* Aroma search (client-side over fetched aromas or fallback list) */}
                                <div className="mb-3">
                                  <Input
                                    placeholder="Поиск аромата"
                                    value={aromaQuery}
                                    onValueChange={(v: string) => setAromaQuery(v)}
                                    startContent={<Icon icon="lucide:search" width={16} />}
                                    className="w-full"
                                  />
                                </div>

                                <CheckboxGroup
                                  value={selectedScents}
                                  onValueChange={setSelectedScents}
                                  className="gap-1"
                                >
                                  {
                                    // If API returned aromas, show them (page-sized). If backend provides `next` then a "Показать ещё" button will load more.
                                    (aromas && aromas.length > 0) ?
                                      // apply client-side aromaQuery filter
                                      (aromas
                                        .filter(a => !aromaQuery || String(a.name).toLowerCase().includes(aromaQuery.toLowerCase()))
                                        .map(a => (
                                          <Checkbox key={a.name} value={a.name}>
                                            {a.name}
                                          </Checkbox>
                                        )))
                                    : // fallback to deriving scents from catalog (legacy behaviour: show first 4 and allow toggle)
                                      (showAllAromas ? uniqueScents : uniqueScents.slice(0, 4))
                                        .filter(s => !aromaQuery || s.toLowerCase().includes(aromaQuery.toLowerCase()))
                                        .map(scent => (
                                          <Checkbox key={scent} value={scent}>
                                            {scent}
                                          </Checkbox>
                                        ))
                                  }
                                </CheckboxGroup>

                                {/* Show more / collapse button */}
                                <div className="mt-3">
                                  {/* If we have API aromas, show load-more button only when backend indicates more via `next` */}
                                  {(aromas && aromas.length > 0) ? (
                                    aromasNext ? (
                                      <Button
                                        variant="flat"
                                        size="sm"
                                        color="default"
                                        onPress={() => loadAromasNext()}
                                        isDisabled={aromasLoadingMore}
                                      >
                                        {aromasLoadingMore ? 'Загрузка...' : 'Показать ещё'}
                                      </Button>
                                    ) : null
                                  ) : (
                                    // fallback toggle for derived scents
                                    (uniqueScents.length > 4) ? (
                                      <Button
                                        variant="flat"
                                        size="sm"
                                        color="default"
                                        onPress={() => setShowAllAromas(prev => !prev)}
                                      >
                                        {showAllAromas ? 'Свернуть' : 'Показать ещё'}
                                      </Button>
                                    ) : null
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Wax Filter (checkbox list, values from backend) — for candles and sachets */}
                          {(isCandleCategory || isSachetCategory) && (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base">Воск</h4>
                                <button
                                  aria-expanded={openFilters.wax}
                                  aria-controls="filter-wax"
                                  onClick={() => toggleFilterSection('wax')}
                                  className="p-1 text-foreground/60 hover:text-foreground transition-transform"
                                >
                                  <Icon icon="lucide:chevron-down" width={18} className={openFilters.wax ? 'transform rotate-180' : 'transform rotate-0'} />
                                </button>
                              </div>

                              {openFilters.wax && (
                                <div id="filter-wax" className="space-y-2">
                                  <CheckboxGroup
                                    value={selectedWax}
                                    onValueChange={(vals) => setSelectedWax(vals.map(v => String(v)))}
                                    className="gap-1"
                                  >
                                    {(waxes || []).map(w => (
                                      <Checkbox key={w.id || w.name} value={w.id != null ? String(w.id) : w.name}>
                                        {w.name}
                                      </Checkbox>
                                    ))}
                                  </CheckboxGroup>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Color Filter (checkbox list, values from backend) */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-base">Цвет</h4>
                              <button
                                aria-expanded={openFilters.color}
                                aria-controls="filter-color"
                                onClick={() => toggleFilterSection('color')}
                                className="p-1 text-foreground/60 hover:text-foreground transition-transform"
                              >
                                <Icon icon="lucide:chevron-down" width={18} className={openFilters.color ? 'transform rotate-180' : 'transform rotate-0'} />
                              </button>
                            </div>

                            {openFilters.color && (
                              <div id="filter-color" className="space-y-2">
                                <CheckboxGroup
                                  value={selectedColor}
                                  onValueChange={(vals) => setSelectedColor(vals.map(v => String(v)))}
                                  className="gap-1"
                                >
                                  {(colors || []).map(c => (
                                    <Checkbox key={c.id || c.name} value={c.id != null ? String(c.id) : c.name}>
                                      {c.name}
                                    </Checkbox>
                                  ))}
                                </CheckboxGroup>
                              </div>
                            )}
                          </div>

                          {/* Holder Filter (checkbox list, values from backend) – hidden for sachets and decorative candles */}
                          {!isHolderNotRelevantCategory && (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base">{isCandleCategory ? 'Подсвечник' : ((isDiffuserCategory || isAutoPerfumeCategory) ? 'Флакон' : 'Тара')}</h4>
                                <button
                                  aria-expanded={openFilters.holder}
                                  aria-controls="filter-holder"
                                  onClick={() => toggleFilterSection('holder')}
                                  className="p-1 text-foreground/60 hover:text-foreground transition-transform"
                                >
                                  <Icon icon="lucide:chevron-down" width={18} className={openFilters.holder ? 'transform rotate-180' : 'transform rotate-0'} />
                                </button>
                              </div>

                              {openFilters.holder && (
                                <div id="filter-holder" className="space-y-2">
                                  <CheckboxGroup
                                    value={selectedHolder}
                                    onValueChange={(vals) => setSelectedHolder(vals.map(v => String(v)))}
                                    className="gap-1"
                                  >
                                    {(holders || []).map(h => (
                                      <Checkbox key={h.id || h.name} value={h.id != null ? String(h.id) : h.name}>
                                        {h.name}
                                      </Checkbox>
                                    ))}
                                  </CheckboxGroup>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Collections filter – доступен для всех категорий */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-base">Коллекции</h4>
                              <button
                                aria-expanded={openFilters.collection}
                                aria-controls="filter-collection"
                                onClick={() => toggleFilterSection('collection')}
                                className="p-1 text-foreground/60 hover:text-foreground transition-transform"
                              >
                                <Icon icon="lucide:chevron-down" width={18} className={openFilters.collection ? 'transform rotate-180' : 'transform rotate-0'} />
                              </button>
                            </div>
                            {openFilters.collection && (
                              <div id="filter-collection" className="space-y-2">
                                <CheckboxGroup
                                  value={selectedCollection ? [selectedCollection] : []}
                                  onValueChange={(vals) => {
                                    const normalized = vals.map(v => String(v));
                                    setSelectedCollection(normalized.includes('new_year') ? 'new_year' : null);
                                  }}
                                  className="gap-1"
                                >
                                  <Checkbox key="new_year" value="new_year">
                                    Новогодняя
                                  </Checkbox>
                                </CheckboxGroup>
                              </div>
                            )}
                          </div>
                        </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Products Grid */}
                <div className={`${filtersVisible ? 'col-span-1 lg:col-span-3' : 'col-span-full'}`} id="products-grid">
                  {productsLoading ? (
                    <LoadingDisplay message="Загрузка товаров..." />
                  ) : productsError ? (
                    <ErrorDisplay 
                      message="Не удалось загрузить товары. Пожалуйста, попробуйте позже." 
                    />
                  ) : filteredProducts.length > 0 ? (
                    <>
                    <motion.div 
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      {filteredProducts.map((product, _idx) => (
                        <motion.div key={product.id} variants={itemVariants}>
                          <Card 
                            key={product.id}
                            className="border border-divider bg-content2/50 product-card-hover product-card h-full flex flex-col"
                            disableRipple
                          >
                            <CardBody className="p-0 overflow-hidden flex flex-col">
                                      <Link to={`/product-detail/${product.slug || product.id}`} onClick={() => {
                                        try {
                                          pushProductClick({ id: product.slug || product.id, name: product.name, price: product.price, category: product.category, sku: product.article, position: _idx + 1, list: selectedCategory || 'catalog' }, selectedCategory || 'catalog');
                                        } catch (e) { /* ignore */ }
                                      }}> 
                                <div className="relative h-80 overflow-hidden cursor-pointer group">
                                  <img 
                                    src={product.image} 
                                    alt={product.name} 
                                    onError={handleImgError}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  />
                                  <div className="img-fallback-overlay absolute inset-0 flex items-center justify-center bg-background/60 text-foreground/70 text-sm">
                                    Изображение временно не доступно
                                  </div>
                                  {/* Badges stack */}
                                    <div className="absolute top-4 left-4 z-20 flex flex-col gap-2 items-start">
                                      {((product.quantity != null) && Number(product.quantity) <= 0) && (
                                        <div className="bg-red-600 text-white px-3 py-1 rounded-md shadow-sm">
                                          <p className="text-xs font-medium">Нет в наличии</p>
                                        </div>
                                      )}
                                      {product.is_exclusive && (
                                        <div className="inline-flex items-center gap-1 rounded-full bg-purple-700/90 text-white px-3 py-1 text-[11px] font-medium tracking-wide uppercase shadow-sm">
                                          <Icon icon="lucide:sparkles" width={14} />
                                          <span>Эксклюзив</span>
                                        </div>
                                      )}
                                      {product.new_year && (
                                        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1 text-[11px] font-medium tracking-wide uppercase shadow-sm">
                                          <Icon icon="lucide:sparkles" width={14} />
                                          <span>Новогодняя коллекция</span>
                                        </div>
                                      )}
                                      {product.labels && product.labels.includes("new") && (
                                        <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full shadow-sm">
                                          <p className="text-xs font-medium">Новинка</p>
                                        </div>
                                      )}
                                      {product.labels && product.labels.includes("best_seller") && (
                                        <div className="bg-foreground text-background px-3 py-1 rounded-full shadow-sm">
                                          <p className="text-xs font-medium">Бестселлер</p>
                                        </div>
                                      )}
                                    </div>
                                </div>
                              </Link>

                              {/* body content: article, title, price, scent */}
                                      <div className="p-6">
                                <div className="flex justify-between w-full items-start">
                                  <div className="pr-4">
                                    {product.article && (
                                      <p className="text-xs text-foreground/60 mb-1">Артикул: {product.article}</p>
                                    )}
                                      <Link to={`/product-detail/${product.slug || product.id}`} onClick={() => {
                                        try {
                                          pushProductClick({ id: product.slug || product.id, name: product.name, price: product.price, category: product.category, sku: product.article, position: _idx + 1, list: selectedCategory || 'catalog' }, selectedCategory || 'catalog');
                                        } catch (e) { /* ignore */ }
                                      }}> 
                                        <h3 className="text-base font-medium hover:underline cursor-pointer whitespace-normal break-words">{product.name}</h3>
                                      </Link>
                                  </div>
                                  <p className="font-medium whitespace-nowrap">{formatPrice(product.price)}&nbsp;₽</p>
                                </div>
                                <p className="text-sm text-foreground/70 mt-3">{product.scent}</p>
                              </div>
                            </CardBody>

                            {/* <Divider /> */}
                            {/* spacer grows to push footer to bottom, keeping divider aligned under body */}
                            <div className="flex-grow" />

                              <CardFooter className="p-6">
                                {((product.quantity != null) && Number(product.quantity) <= 0) ? (
                                  <Button
                                    color="default"
                                    variant="flat"
                                    className="w-full"
                                    isDisabled={true}
                                  >
                                    Нет в наличии
                                  </Button>
                                ) : (
                                  <Button 
                                    color="primary" 
                                    variant="flat" 
                                    className="w-full"
                                    endContent={<Icon icon="lucide:shopping-bag" width={18} />}
                                    onPress={() => {
                                      // guard: don't add if out of stock
                                      if ((product.quantity != null) && Number(product.quantity) <= 0) return;
                                      addItem({
                                        id: product.id,
                                        name: product.name,
                                        slug: product.slug,
                                        category: product.category,
                                        price: product.price,
                                        image: product.image
                                      });
                                      onAddToCart(product.name);
                                    }}
                                  >
                                    В корзину
                                  </Button>
                                )}
                              </CardFooter>
                          </Card>
                        </motion.div>
                      ))}
                    </motion.div>

                    {/* Load more button / count */}
                    <div className="mt-8 flex flex-col items-center col-span-full">
                      {typeof count === 'number' && (
                        <p className="text-sm text-foreground/70 mb-4">Показано {filteredProducts.length} из {count}</p>
                      )}
                      {next && (
                        <div>
                          <Button
                            color="primary"
                            variant="flat"
                            onPress={() => loadNext()}
                            isDisabled={isLoadingMore}
                          >
                            {isLoadingMore ? 'Загрузка...' : 'Показать ещё'}
                          </Button>
                        </div>
                      )}
                    </div>
                    </>
                  ) : (
                    <div className="text-center py-16">
                      <Icon icon="lucide:package-x" className="mx-auto mb-4 text-foreground/40" width={48} />
                      <h3 className="text-xl font-medium mb-2">Товары не найдены</h3>
                      <p className="text-foreground/70 mb-6">
                        Попробуйте изменить параметры фильтрации или выбрать другую категорию
                      </p>
                      <Button 
                        color="primary" 
                        variant="flat"
                        onPress={handleResetFilters}
                      >
                        Сбросить фильтры
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            // Categories View
            <>
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-light mb-4">Категории <span className="font-medium">товаров</span></h2>
                <p className="text-foreground/70 max-w-2xl mx-auto">
                  Выберите категорию, чтобы просмотреть все доступные товары
                </p>
              </div>

              <form
                onSubmit={handleCatalogSearchSubmit}
                className="w-full max-w-3xl mx-auto mb-12 flex flex-col sm:flex-row gap-3"
              >
                <Input
                  variant="bordered"
                  size="lg"
                  placeholder="Поиск по всему каталогу..."
                  value={catalogSearchValue}
                  onValueChange={setCatalogSearchValue}
                  startContent={<Icon icon="lucide:search" className="text-foreground/50" width={18} />}
                  endContent={catalogSearchValue && (
                    <button
                      type="button"
                      className="text-foreground/60"
                      onClick={() => setCatalogSearchValue("")}
                    >
                      <Icon icon="lucide:x" width={16} />
                    </button>
                  )}
                  classNames={{ inputWrapper: 'rounded-none' }}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  className="rounded-none w-full sm:w-auto"
                  isDisabled={!catalogSearchValue.trim()}
                >
                  Искать
                </Button>
              </form>

              {categoriesLoading ? (
                <LoadingDisplay message="Загрузка категорий..." />
              ) : categoriesError ? (
                <ErrorDisplay message="Не удалось загрузить категории товаров. Пожалуйста, попробуйте позже." />
              ) : (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {categories.map((category) => (
                    <motion.div key={category.key} variants={itemVariants}>
                      <Link to={`/catalog/${category.key}`}>
                        <Card 
                          className="border border-divider bg-content1 h-full flex flex-col product-card-hover"
                          isPressable
                          disableRipple
                        >
                          <CardBody className="p-0 overflow-hidden flex flex-col">
                            <div className="h-56 overflow-hidden relative">
                              <img 
                                src={category.image} 
                                alt={category.name} 
                                onError={handleImgError}
                                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                              />
                              <div className="img-fallback-overlay absolute inset-0 flex items-center justify-center bg-background/60 text-foreground/70 text-sm">
                                Изображение временно не доступно
                              </div>
                            </div>
                            <div className="p-6 flex flex-col justify-between flex-1">
                              <div>
                                <h3 className="text-xl font-medium mb-2">{category.name}</h3>
                                <p className="text-foreground/70 mb-6">{category.description}</p>
                              </div>
                              <div>
                                <Button 
                                  color="primary" 
                                  variant="flat" 
                                  endContent={<Icon icon="lucide:arrow-right" width={16} />}
                                >
                                  Смотреть товары
                                </Button>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection />
    </>
  );
};